import { PRICE_GBP } from '@bandcheck/server/modules/pricing'
import { useState } from 'react'
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { colors, fontFamily, fontSize, radius, shadow, space } from '../tokens'

type Props = {
  onSubmit: (input: { postcode: string; houseNumber: string; moveInDate?: string }) => void
  isPending: boolean
  errorMessage?: string
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
// Real UK postcode shape (incl. the GIR 0AA Girobank special case), tested
// against a normalised value so "SW1A2AA" and "sw1a 2aa" both pass. Catches
// paste-an-address / typo inputs inline before submit, instead of letting them
// bounce off the server's PostcodeSchema (min5/max10/charset) as a raw Zod 400.
const UK_POSTCODE = /^(GIR ?0AA|[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i
const normalisePostcode = (v: string) => v.trim().toUpperCase().replace(/\s+/g, ' ')

export const PostcodeForm = ({ onSubmit, isPending, errorMessage }: Props) => {
  const [postcode, setPostcode] = useState('')
  const [postcodeTouched, setPostcodeTouched] = useState(false)
  const [houseNumber, setHouseNumber] = useState('')
  const [moveInDate, setMoveInDate] = useState('')
  const moveInDateValid = moveInDate.length === 0 || ISO_DATE.test(moveInDate)
  const postcodeValid = UK_POSTCODE.test(normalisePostcode(postcode))
  // Only surface the error once they've left the field — never nag mid-type.
  const showPostcodeError = postcodeTouched && postcode.trim().length > 0 && !postcodeValid
  const canSubmit = postcodeValid && houseNumber.trim().length > 0 && moveInDateValid && !isPending

  return (
    <View style={styles.container}>
      <View style={styles.eyebrowRow}>
        <View style={styles.eyebrowDot} />
        <Text style={styles.eyebrow}>Based on official Valuation Office records</Text>
      </View>
      <Text style={styles.title}>
        Your council tax band might be <Text style={styles.titleHl}>quietly wrong</Text>.
      </Text>
      <Text style={styles.subtitle}>
        Enter your postcode and house number. We compare you to your neighbours, weigh the evidence,
        and tell you whether it's worth challenging — in seconds.
      </Text>

      <View style={styles.card}>
        <View style={styles.cardHead}>
          <Text style={styles.cardTitle}>Check your band — free</Text>
          <Text style={styles.cardBadge}>NO SIGNUP</Text>
        </View>

        <Text style={styles.label}>Postcode</Text>
        <TextInput
          accessibilityLabel="Postcode"
          autoCapitalize="characters"
          autoCorrect={false}
          editable={!isPending}
          maxLength={8}
          onBlur={() => setPostcodeTouched(true)}
          onChangeText={setPostcode}
          placeholder="e.g. SW1A 2AA"
          placeholderTextColor={colors.faint}
          style={[styles.input, showPostcodeError && styles.inputError]}
          value={postcode}
        />
        {showPostcodeError ? (
          <Text style={styles.fieldError}>
            That doesn't look like a UK postcode — enter just the postcode, e.g. SW1A 2AA.
          </Text>
        ) : null}

        <Text style={styles.label}>House number or name</Text>
        <TextInput
          accessibilityLabel="House number"
          autoCorrect={false}
          editable={!isPending}
          onChangeText={setHouseNumber}
          placeholder="e.g. 10"
          placeholderTextColor={colors.faint}
          style={styles.input}
          value={houseNumber}
        />

        <Text style={styles.label}>
          When did you move in? <Text style={styles.optional}>(optional)</Text>
        </Text>
        <Text style={styles.helper}>
          Sharpens the refund estimate. Leave blank if you'd rather not say.
        </Text>
        <TextInput
          accessibilityLabel="Move-in date"
          autoCorrect={false}
          editable={!isPending}
          onChangeText={setMoveInDate}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={colors.faint}
          style={[styles.input, moveInDate.length > 0 && !moveInDateValid && styles.inputError]}
          value={moveInDate}
        />

        <Pressable
          accessibilityRole="button"
          disabled={!canSubmit}
          onPress={() =>
            onSubmit({
              postcode: normalisePostcode(postcode),
              houseNumber: houseNumber.trim(),
              moveInDate: moveInDate.length > 0 ? moveInDate : undefined,
            })
          }
          style={[styles.button, !canSubmit && styles.buttonDisabled]}
        >
          <Text style={styles.buttonText}>{isPending ? 'Checking…' : 'Check my band  →'}</Text>
        </Pressable>

        <Text style={styles.foot}>
          Instant result · We never store your details without consent. If you're overbanded, the
          appeal pack is <Text style={styles.footStrong}>{PRICE_GBP}</Text> — refunded if your
          challenge fails.
        </Text>

        {errorMessage ? <Text style={styles.error}>{errorMessage}</Text> : null}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: { width: '100%', maxWidth: 480 },
  eyebrowRow: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: space[8],
    paddingHorizontal: 13,
    paddingVertical: 7,
    backgroundColor: colors.primary[100],
    borderRadius: radius.pill,
    marginBottom: space[16],
  },
  eyebrowDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.positive,
  },
  eyebrow: {
    fontSize: 12.5,
    fontWeight: '600',
    letterSpacing: 0.25,
    color: colors.primary[700],
    fontFamily: fontFamily.body,
  },
  title: {
    fontSize: fontSize.hero,
    fontWeight: '700',
    color: colors.ink,
    lineHeight: 42,
    marginBottom: space[16],
    fontFamily: fontFamily.body,
    letterSpacing: -1,
  },
  titleHl: {
    color: colors.primary[700],
  },
  subtitle: {
    fontSize: 17,
    color: colors.muted,
    lineHeight: 25,
    marginBottom: space[24],
    fontFamily: fontFamily.body,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 28,
    ...shadow.card,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.ink,
    letterSpacing: -0.2,
    fontFamily: fontFamily.body,
  },
  cardBadge: {
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    color: colors.positive,
    backgroundColor: colors.positiveSoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: radius.pill,
    overflow: 'hidden',
    fontFamily: fontFamily.body,
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: space[16],
    marginBottom: space[8],
    color: colors.muted,
    fontFamily: fontFamily.body,
  },
  optional: { color: colors.faint, fontWeight: '400' },
  helper: {
    fontSize: fontSize.tiny,
    color: colors.faint,
    marginTop: -4,
    marginBottom: space[8],
    fontFamily: fontFamily.body,
  },
  inputError: { borderColor: colors.error[700], borderWidth: 2 },
  fieldError: {
    fontSize: fontSize.tiny,
    color: colors.error[900],
    marginTop: space[8],
    fontFamily: fontFamily.body,
  },
  input: {
    borderWidth: 1.5,
    borderColor: colors.borderStrong,
    borderRadius: radius.sm,
    paddingHorizontal: space[16],
    height: 54,
    fontSize: 17,
    backgroundColor: colors.surface,
    color: colors.ink,
    fontFamily: fontFamily.body,
  },
  button: {
    marginTop: space[16],
    backgroundColor: colors.primary[700],
    height: 56,
    borderRadius: radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadow.subtle,
  },
  buttonDisabled: { backgroundColor: colors.borderStrong, shadowOpacity: 0 },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16.5,
    fontWeight: '600',
    fontFamily: fontFamily.body,
  },
  foot: {
    marginTop: 18,
    fontSize: 12.5,
    color: colors.faint,
    lineHeight: 19,
    textAlign: 'center',
    fontFamily: fontFamily.body,
  },
  footStrong: {
    color: colors.muted,
    fontWeight: '700',
  },
  error: {
    color: colors.error[900],
    backgroundColor: colors.error[100],
    borderLeftWidth: 4,
    borderLeftColor: colors.error[700],
    padding: space[16],
    marginTop: space[16],
    borderRadius: radius.sm,
    fontFamily: fontFamily.body,
    fontSize: fontSize.small,
  },
})
